import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import {
  RefreshCw,
  Compass,
  Star,
  Shield,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUpLeft,
  ArrowDownLeft,
} from "lucide-react";
import { api } from "../../services/api";
import AstriaJapanV2Shell, {
  SHELL_COLORS_V2,
  JP2_FONT,
} from "./AstriaJapanV2Shell";
import { getStoredAstriaJapanDob } from "../../utils/astriaJapanKyusei";

// ─── Astria Japan V3 Design — Language Consistent ─────────────────────────
// Uses either Japanese or English based on user preference
// No mixing of languages

const JP_FONT = JP2_FONT;
const C = {
  ...SHELL_COLORS_V2,
  accent: "#D4735D",
  accentLight: "#FDECE8",
  accentSubtle: "#FFF5F2",
  sage: "#E8F0E4",
  sageLight: "#F5F9F3",
  textPrimary: "#2B211D",
  textSecondary: "#5A4A42",
  textMuted: "#8A7A73",
  borderLight: "#F0E8E4",
  cardBg: "#FFFFFF",
  gradientStart: "#FDF7F5",
  gradientEnd: "#FFF0EC",
};

interface Star {
  id: number;
  code: string;
  element: string;
  primaryColor: string;
  secondaryColor: string;
  keywords: string[];
  description: string;
  omamori: string;
  hashtags: string[];
}

interface DailyResponse {
  success: boolean;
  starId: number;
  star: Star;
  direction: { directionCode: string; labelJP: string; suggestion: string };
  view: {
    header: { title: string; subtext: string };
    sections: Array<{ id: string; title: string; type: string; data: any }>;
  };
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// ─── Direction Icon Helper ─────────────────────────────────────────────────
const getDirectionIcon = (directionCode: string) => {
  const iconProps = { className: "w-5 h-5", style: { color: "#5A7A4E" } };
  switch (directionCode?.toUpperCase()) {
    case "N":
      return <ArrowUp {...iconProps} />;
    case "NE":
      return <ArrowUpRight {...iconProps} />;
    case "E":
      return <ArrowRight {...iconProps} />;
    case "SE":
      return <ArrowDownRight {...iconProps} />;
    case "S":
      return <ArrowDown {...iconProps} />;
    case "SW":
      return <ArrowDownLeft {...iconProps} />;
    case "W":
      return <ArrowLeft {...iconProps} />;
    case "NW":
      return <ArrowUpLeft {...iconProps} />;
    default:
      return <Compass {...iconProps} />;
  }
};

const SectionBlock: React.FC<{
  index: number;
  background?: string;
  icon?: React.ReactNode;
  iconBg?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}> = ({
  index,
  background,
  icon,
  iconBg,
  title,
  subtitle,
  children,
  className = "",
}) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    style={{
      background: background || "transparent",
      borderRadius: 16,
      padding: "28px 32px",
    }}
    className={className}
  >
    <div className="flex items-start gap-4 mb-4">
      {icon && (
        <div
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: iconBg || "rgba(212,115,93,0.12)",
          }}
        >
          {icon}
        </div>
      )}
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: C.textPrimary,
            letterSpacing: "0.03em",
            marginBottom: subtitle ? 4 : 0,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
    {children}
  </motion.div>
);

const KyuseiJapanResult: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DailyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  // Get language preference from localStorage or session
  const getPreferredLanguage = (): "ja" | "en" => {
    const lang = localStorage.getItem("preferredLanguage") || "ja";
    return lang === "en" ? "en" : "ja";
  };

  const language = getPreferredLanguage();

  // Language-specific content
  const content = {
    ja: {
      brand: "九星気質",
      checkIn: "今日のチェックイン",
      yourStar: "あなたの星の気質",
      element: "五行",
      luckyColor: "ラッキーカラー",
      omamori: "今日のお守り",
      direction: "今日の吉方位",
      action1: "方角を意識して過ごす",
      action2: "新しいご縁につながる行動を試す",
      action3: "静かな場所でひと息つく",
      refresh: "今日の流れを再確認",
      error: "お守りの情報が見つかりませんでした。",
      loading: "読み込み中...",
      back: "今日の流れを見る",
      footer: "あなたの心に、そっと寄り添う時間を。",
      subtext: "今日は、あなたの星と心のタイミングを静かに見てみましょう。",
    },
    en: {
      brand: "Kyusei",
      checkIn: "Today's Check-in",
      yourStar: "Your Star Identity",
      element: "Element",
      luckyColor: "Lucky Color",
      omamori: "Today's Omamori",
      direction: "Today's Lucky Direction",
      action1: "Be mindful of the direction",
      action2: "Try actions that invite new connections",
      action3: "Take a moment in a quiet place",
      refresh: "Review Today's Flow",
      error: "Omamori information not found.",
      loading: "Loading...",
      back: "View Today's Flow",
      footer: "A gentle moment for your heart.",
      subtext: "Today, let's quietly observe your star and heart's timing.",
    },
  };

  const t = content[language];

  useEffect(() => {
    const raw = sessionStorage.getItem("astriaJapanKyuseiDaily");
    if (raw) {
      try {
        setData(JSON.parse(raw));
        return;
      } catch {
        // fall through to a fresh fetch below
      }
    }

    const storedDob = getStoredAstriaJapanDob();
    if (!storedDob) {
      setError(t.error);
      return;
    }

    api
      .post("/backend/astria-japan-kyusei/daily", { dob: storedDob })
      .then((response) => {
        if (response.data?.success) {
          sessionStorage.setItem(
            "astriaJapanKyuseiDaily",
            JSON.stringify(response.data),
          );
          setData(response.data);
        } else {
          setError(t.error);
        }
      })
      .catch(() => setError(t.error));
  }, [t.error]);

  const handleNewCheckIn = () => {
    sessionStorage.removeItem("astriaJapanKyuseiDaily");
    sessionStorage.removeItem("astriaJapanKyuseiTiming");
    navigate("/astria-japan/kyusei");
  };

  const handleShare = async () => {
    if (!data) return;
    setSharing(true);
    try {
      const { star } = data;
      const container = document.createElement("div");
      container.style.width = "1080px";
      container.style.height = "1350px";
      container.style.padding = "80px 64px";
      container.style.boxSizing = "border-box";
      container.style.background = star.secondaryColor || "#FDF7FF";
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.justifyContent = "center";
      container.style.alignItems = "center";
      container.style.fontFamily = "system-ui, -apple-system, sans-serif";

      container.innerHTML = `
        <div style="width:100%; background:#FFFFFF; border-radius:32px; padding:56px; box-shadow:0 20px 60px rgba(0,0,0,0.08); text-align:center;">
          <div style="width:64px; height:64px; border-radius:999px; background:${star.primaryColor}; margin:0 auto 28px;"></div>
          <div style="font-size:30px; font-weight:600; color:#222; margin-bottom:8px;">${star.code}</div>
          <div style="font-size:15px; color:#777; margin-bottom:32px;">${language === "ja" ? "今日のお守り" : "Today's Omamori"}</div>
          <p style="font-size:24px; line-height:1.8; color:#444; margin:0 0 36px;">${star.omamori}</p>
          <div style="display:flex; gap:10px; justify-content:center; flex-wrap:wrap;">
            ${star.hashtags
              .map(
                (tag) =>
                  `<span style="font-size:16px; color:${star.primaryColor}; background:rgba(0,0,0,0.04); padding:8px 18px; border-radius:999px;">${tag}</span>`,
              )
              .join("")}
          </div>
        </div>
        <div style="margin-top:32px; font-size:14px; color:#999;">${language === "ja" ? "九星気質" : "Kyusei"}</div>
      `;

      document.body.appendChild(container);
      const canvas = await html2canvas(container, {
        scale: 2,
        backgroundColor: star.secondaryColor || "#FDF7FF",
        useCORS: true,
        logging: false,
      });
      document.body.removeChild(container);

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `kyusei-omamori-${star.id}-${Date.now()}.png`;
      link.click();
    } catch (e) {
      console.error("Failed to generate Omamori share image:", e);
    } finally {
      setSharing(false);
    }
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#FDF7F5", fontFamily: JP_FONT }}
      >
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: "#B4574A" }}>
            {error}
          </p>
          <button
            onClick={() => navigate("/astria-japan/kyusei")}
            className="px-6 py-2.5 rounded-full text-white text-xs font-semibold"
            style={{ background: C.accent }}
          >
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#FDF7F5" }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: `${C.accent}33`, borderTopColor: C.accent }}
        />
      </div>
    );
  }

  const { star, direction, view } = data;
  const actionItems =
    language === "ja"
      ? [
          `${direction.labelJP}${t.action1.replace("方角を意識して過ごす", "")}`,
          t.action2,
          t.action3,
        ]
      : [
          `Be mindful of the ${direction.labelJP} direction`,
          t.action2,
          t.action3,
        ];

  return (
    <AstriaJapanV2Shell active="viral" onShare={handleShare} sharing={sharing}>
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          marginBottom: 32,
          padding: "32px 36px",
          background: `linear-gradient(135deg, ${C.gradientStart} 0%, ${C.gradientEnd} 100%)`,
          borderRadius: 20,
          border: "1px solid rgba(212,115,93,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: C.textMuted,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          {t.brand}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 400,
            color: C.textPrimary,
            letterSpacing: "0.02em",
            lineHeight: 1.5,
          }}
        >
          {language === "ja" ? view.header.title : "Your Star & Heart's Timing"}
        </div>
        <div
          style={{
            fontSize: 13,
            color: C.textMuted,
            marginTop: 6,
            lineHeight: 1.6,
          }}
        >
          {language === "ja" ? view.header.subtext : t.subtext}
        </div>
      </motion.div>

      <div className="flex flex-col gap-6">
        {/* ─── 1) Star Identity ────────────────────────────────────────── */}
        <SectionBlock
          index={0}
          background={C.cardBg}
          icon={<Star className="w-5 h-5" style={{ color: "#FFFFFF" }} />}
          iconBg={star.primaryColor}
          title={t.yourStar}
        >
          <div className="flex items-center gap-4 mb-4">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${star.primaryColor} 0%, ${star.secondaryColor} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                fontSize: 22,
                fontWeight: 600,
                flexShrink: 0,
                boxShadow: `0 4px 20px ${star.primaryColor}33`,
              }}
            >
              {star.id}
            </div>
            <div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: C.textPrimary,
                  letterSpacing: "0.02em",
                }}
              >
                {star.code}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: C.textMuted,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {t.element} {star.element}
                <span
                  style={{
                    display: "inline-block",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: C.textMuted,
                  }}
                />
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: star.primaryColor,
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  />
                  <span
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: star.secondaryColor,
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  />
                  {t.luckyColor}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {star.keywords.map((kw) => (
              <span
                key={kw}
                style={{
                  display: "inline-block",
                  padding: "2px 12px",
                  borderRadius: 999,
                  background: C.accentSubtle,
                  fontSize: 12,
                  fontWeight: 400,
                  color: C.textSecondary,
                }}
              >
                {kw}
              </span>
            ))}
          </div>

          <p
            style={{
              fontSize: 14,
              lineHeight: 1.8,
              color: C.textSecondary,
              margin: 0,
            }}
          >
            {star.description}
          </p>
        </SectionBlock>

        {/* ─── 2) Omamori ──────────────────────────────────────────────── */}
        <SectionBlock
          index={1}
          background={C.accentLight}
          icon={<Shield className="w-5 h-5" style={{ color: C.accent }} />}
          iconBg="rgba(212,115,93,0.15)"
          title={t.omamori}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: C.textMuted,
              letterSpacing: "0.08em",
              marginBottom: 12,
            }}
          >
            ✦ {t.omamori} ✦
          </div>

          <p
            style={{
              fontSize: 17,
              lineHeight: 1.9,
              color: C.textPrimary,
              margin: "0 0 16px 0",
              fontWeight: 400,
              fontStyle: "italic",
            }}
          >
            {star.omamori}
          </p>

          <div className="flex flex-wrap gap-2">
            {star.hashtags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: "inline-block",
                  padding: "3px 14px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,0.7)",
                  fontSize: 12,
                  fontWeight: 400,
                  color: star.primaryColor,
                  border: "1px solid rgba(212,115,93,0.1)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </SectionBlock>

        {/* ─── 3) Direction ────────────────────────────────────────────── */}
        <SectionBlock
          index={2}
          background={C.sageLight}
          icon={getDirectionIcon(direction.directionCode)}
          iconBg="rgba(122,155,109,0.15)"
          title={t.direction}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: C.textMuted,
              letterSpacing: "0.08em",
              marginBottom: 12,
            }}
          >
            ✦ {t.direction} ✦
          </div>

          <div
            style={{
              display: "inline-block",
              padding: "4px 20px",
              borderRadius: 999,
              background: "rgba(122,155,109,0.12)",
              fontSize: 16,
              fontWeight: 500,
              color: "#5A7A4E",
              marginBottom: 14,
            }}
          >
            {direction.labelJP}
          </div>

          <p
            style={{
              fontSize: 14,
              lineHeight: 1.8,
              color: C.textSecondary,
              margin: "0 0 16px 0",
            }}
          >
            {direction.suggestion}
          </p>

          <div className="flex flex-col gap-2">
            {actionItems.map((item, idx) => (
              <motion.div
                key={item}
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + idx * 0.08 }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "rgba(122,155,109,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#7A9B6D",
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 13,
                    color: C.textSecondary,
                    fontWeight: 400,
                  }}
                >
                  {item}
                </span>
              </motion.div>
            ))}
          </div>
        </SectionBlock>

        {/* ─── 4) Footer ────────────────────────────────────────────────── */}
        <motion.div
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          style={{
            textAlign: "center",
            padding: "24px 20px",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: C.textMuted,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {t.brand}
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.textMuted,
              marginTop: 4,
              lineHeight: 1.6,
            }}
          >
            {t.footer}
          </div>
        </motion.div>
      </div>

      {/* ─── Actions ────────────────────────────────────────────────────── */}
      <motion.div
        className="flex justify-end mt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <button
          onClick={handleNewCheckIn}
          className="flex items-center justify-center gap-2 rounded-full py-2.5 px-6 text-sm font-medium transition-all hover:bg-gray-50 active:scale-95"
          style={{
            border: "1px solid rgba(43,33,29,0.1)",
            color: C.textSecondary,
            background: "transparent",
          }}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t.refresh}
        </button>
      </motion.div>
    </AstriaJapanV2Shell>
  );
};

export default KyuseiJapanResult;
